/**
 * InstallPrompt - Banner de instalacion PWA
 *
 * Diseñado para neurodivergencia:
 * - No intrusivo, aparece solo cuando es util
 * - Facil de descartar sin presion
 * - Explica claramente los beneficios
 * - Recuerda la preferencia del usuario
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Zap, WifiOff } from 'lucide-react';
import { Button } from '@/ui/primitives';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEY = 'exocortex-install-dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 dias

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Verificar si ya esta instalado
  useEffect(() => {
    const checkInstalled = () => {
      // Verificar standalone mode
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      // Verificar iOS standalone
      const isIosStandalone = ('standalone' in window.navigator) && (window.navigator as { standalone?: boolean }).standalone;

      setIsInstalled(isStandalone || Boolean(isIosStandalone));
    };

    checkInstalled();

    // Escuchar cambios en display-mode
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkInstalled);

    return () => mediaQuery.removeEventListener('change', checkInstalled);
  }, []);

  // Verificar si el usuario descarto el prompt recientemente
  const wasDismissedRecently = useCallback(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) return false;

      const dismissedTime = parseInt(dismissed, 10);
      return Date.now() - dismissedTime < DISMISS_DURATION;
    } catch {
      return false;
    }
  }, []);

  // Escuchar evento beforeinstallprompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // Mostrar solo si no fue descartado recientemente
      if (!wasDismissedRecently() && !isInstalled) {
        // Esperar un poco para no interrumpir la carga inicial
        setTimeout(() => setIsVisible(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Escuchar instalacion exitosa
    window.addEventListener('appinstalled', () => {
      setIsVisible(false);
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [wasDismissedRecently, isInstalled]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;

      if (choice.outcome === 'accepted') {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error installing PWA:', error);
    }

    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch {
      // Ignorar errores de localStorage
    }
  };

  // No mostrar si ya esta instalado o no hay prompt disponible
  if (isInstalled || !deferredPrompt) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: 'spring', damping: 25 }}
          className="fixed bottom-4 left-4 right-4 z-[100] sm:left-auto sm:right-4 sm:max-w-sm"
        >
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="flex items-start gap-3 p-4">
              <div className="p-2 bg-blue-500/20 rounded-lg flex-shrink-0">
                <Download className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-100">Instalar Exocortex</h3>
                <p className="text-sm text-slate-400 mt-0.5">
                  Accede mas rapido desde tu pantalla de inicio
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Beneficios */}
            <div className="px-4 pb-3 grid grid-cols-3 gap-2">
              <Benefit icon={Smartphone} text="Acceso rapido" />
              <Benefit icon={Zap} text="Mas fluido" />
              <Benefit icon={WifiOff} text="Funciona offline" />
            </div>

            {/* Acciones */}
            <div className="flex gap-2 p-4 pt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismiss}
                className="flex-1 text-slate-400"
              >
                Ahora no
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleInstall}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-1" />
                Instalar
              </Button>
            </div>

            {/* Nota tranquilizadora */}
            <div className="px-4 pb-3">
              <p className="text-xs text-slate-600 text-center">
                Sin espacio extra. Puedes desinstalar cuando quieras.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface BenefitProps {
  icon: React.ElementType;
  text: string;
}

function Benefit({ icon: Icon, text }: BenefitProps) {
  return (
    <div className="flex flex-col items-center gap-1 p-2 bg-slate-900/50 rounded-lg">
      <Icon className="w-4 h-4 text-slate-400" />
      <span className="text-xs text-slate-500 text-center">{text}</span>
    </div>
  );
}
